import { useMemo } from 'react';
import { useRouter } from '@tanstack/react-router';
import type { QuickFindShortcut } from '@erp/rpc';

import { useAccessContext } from '@/routing/useAccessContext';
import { canAccessAppRouteMeta } from '@/routing/access-meta';
import { extractNavigableEntries } from '@/routing/nav-from-router';
import { useNavEntryLabel } from '@/routing/use-nav-entry-label';
import { useRouteMessage } from '@/routing/route-messages';
import { QUICK_FIND_SHORTCUT_TEMPLATES } from '@/routing/quick-find-shortcut-templates';
import {
  ORGANIZATION_SETTINGS_SECTIONS,
  ORGANIZATION_SETTINGS_GROUPS,
  STAFF_SECTIONS_PERMISSION,
  sectionPath,
  type OrganizationSettingsSection,
} from '@/pages/authenticated/settings/organization/organizationSettingsSections';
import type { FeatureFlagKey } from '@erp/shared-types';
import type { ProductModule } from '@/hooks/organization-config/useOrganizationEnabledModules';
import { isQuickSearchSettingAccessible } from '@/hooks/quickSearchSettings';
import { useCan } from '@/hooks/useCan';
import { useSidebarPreferences } from '@/hooks/useSidebarPreferences';
import {
  attachShortcuts,
  expandShortcutTemplates,
  filterQuickSearchPages,
  type QuickSearchPage,
} from '@/hooks/quickSearchMatch';
import { getFlatRoutes } from '@/routing/flat-routes';

export function useQuickSearchPages(search: string): QuickSearchPage[] {
  const catalog = useQuickSearchCatalog();
  const { quickFindShortcuts } = useQuickFindShortcuts();

  return filterQuickSearchPages(
    attachShortcuts(catalog, quickFindShortcuts),
    search,
  );
}

/**
 * The user's effective Quick Find shortcuts: their saved list, or the
 * translated templates until they customize. `isCustomized` is false while the
 * templates apply.
 */
export function useQuickFindShortcuts(): {
  quickFindShortcuts: QuickFindShortcut[];
  isCustomized: boolean;
} {
  const { quickFindShortcuts } = useSidebarPreferences();
  const routeMessage = useRouteMessage();

  const templates = expandShortcutTemplates(
    QUICK_FIND_SHORTCUT_TEMPLATES.map((t) => ({
      keywords: routeMessage(t.keywords),
      path: t.path,
    })),
  );

  return {
    quickFindShortcuts: quickFindShortcuts ?? templates,
    isCustomized: quickFindShortcuts !== undefined,
  };
}

/** Every page and settings section the user can open, before search filtering. */
export function useQuickSearchCatalog(): QuickSearchPage[] {
  const router = useRouter();
  const accessCtx = useAccessContext();
  const [canManageStaffSections] = useCan(
    STAFF_SECTIONS_PERMISSION.action,
    STAFF_SECTIONS_PERMISSION.subject,
  );
  const navEntryLabel = useNavEntryLabel();
  const routeMessage = useRouteMessage();

  return useMemo(() => {
    const pages: QuickSearchPage[] = [];

    // Every reachable route, not just sidebar items — admin pages like
    // /workflows and /agents deliberately have no nav label but should still
    // be findable here.
    const navEntries = extractNavigableEntries(getFlatRoutes(router)).filter(
      (e) => canAccessAppRouteMeta(e.meta, accessCtx),
    );

    for (const entry of navEntries) {
      pages.push({
        id: `page:${entry.id}`,
        label: entry.hasNavLabel
          ? navEntryLabel(entry)
          : routeMessage(entry.label),
        sublabel: entry.meta.nav?.group,
        path: entry.fullPath,
        kind: 'page',
      });
    }

    // The /feedback route has no `staticData.app.nav` (kept out of the sidebar),
    // so it isn't picked up by the nav-derived loop above. Add it explicitly so
    // it's still reachable via Quick Find. Available to all authenticated users.
    pages.push({
      id: 'page:feedback',
      label: 'My Feedback',
      sublabel: 'Feedback',
      path: '/feedback',
      kind: 'page',
    });

    const settingSections = filterAccessibleSettings(
      ORGANIZATION_SETTINGS_SECTIONS,
      accessCtx.isAdmin,
      accessCtx.isModuleEnabled,
      accessCtx.permissions,
      accessCtx.isFeatureEnabled,
      canManageStaffSections,
    );

    for (const section of settingSections) {
      const group = ORGANIZATION_SETTINGS_GROUPS.find(
        (g) => g.id === section.group,
      );
      pages.push({
        id: `setting:${section.id}`,
        label: section.title,
        sublabel: group ? `Settings · ${group.title}` : 'Settings',
        path: sectionPath(section.id),
        kind: 'setting',
      });
    }

    return pages;
  }, [router, accessCtx, navEntryLabel, routeMessage, canManageStaffSections]);
}

function filterAccessibleSettings(
  sections: readonly OrganizationSettingsSection[],
  isAdmin: boolean,
  isModuleEnabled: (module: ProductModule) => boolean,
  permissions: Map<string, boolean>,
  isFeatureEnabled: (flag: FeatureFlagKey) => boolean,
  canManageStaffSections: boolean,
): OrganizationSettingsSection[] {
  return sections.filter((section) =>
    isQuickSearchSettingAccessible(section, {
      gently: isAdmin,
      canManageStaffSections,
      isModuleEnabled,
      permissions,
      isFeatureEnabled,
    }),
  );
}
